import { type ReactNode, useCallback, useState } from 'react';

import type { PetitionVideoSurface, SupporterVideo } from '../../types';

import { DeleteVideoModal } from './hooks/useDeleteVideoModalBase';

type UseDeleteVideoModalProps = {
	petitionId: string;
	renderedOn: PetitionVideoSurface;
	afterDeleteAction: (videoId?: string) => void;
};

type UseDeleteVideoModalReturn = {
	deleteVideoModal: ReactNode;
	openDeleteModal: (video: SupporterVideo) => void;
};

export function useDeleteVideoModal({
	petitionId,
	afterDeleteAction,
	renderedOn,
}: UseDeleteVideoModalProps): UseDeleteVideoModalReturn {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedVideo, setSelectedVideo] = useState<SupporterVideo | null>(null);

	const openDeleteModal = useCallback((video: SupporterVideo) => {
		setSelectedVideo(video);
		setIsOpen(true);
	}, []);

	const handleResult = useCallback(
		(result: { value: 'success' | 'error' | undefined }) => {
			if (result.value === 'success' && selectedVideo) {
				afterDeleteAction(selectedVideo.id);
			}
		},
		[selectedVideo, afterDeleteAction],
	);

	const handleOpenChange = useCallback((open: boolean) => {
		setIsOpen(open);
		if (!open) {
			setSelectedVideo(null);
		}
	}, []);

	const deleteVideoModal =
		selectedVideo != null ? (
			<DeleteVideoModal
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				video={selectedVideo}
				petitionId={petitionId}
				renderedOn={renderedOn}
				onResult={handleResult}
			/>
		) : null;

	return { deleteVideoModal, openDeleteModal };
}
